import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Facebook, Send, MapPin, Mail, Phone, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Reveal, SectionEyebrow } from '../components/ui-bits';
import { postContact } from '../lib/api';
import Seo from '../components/Seo';

const ENQUIRY_TYPES = [
  { v: 'representation', l: 'Representation' },
  { v: 'casting', l: 'Casting' },
  { v: 'business', l: 'Business' },
  { v: 'general', l: 'General' },
];

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', enquiry_type: 'representation', subject: '', message: '', hp: '' });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.hp) return; // honeypot triggered — silently ignore
    if (!consent) {
      toast.error('Please tick the consent box so we know we can reply to you.');
      return;
    }
    setLoading(true);
    try {
      const { hp, ...payload } = form;
      await postContact(payload);
      toast.success('Thank you. Your message has been received. We will be in touch shortly.');
      setForm({ name: '', email: '', phone: '', enquiry_type: 'representation', subject: '', message: '', hp: '' });
      setConsent(false);
    } catch {
      toast.error('Sorry, we could not send your message. Please try again or email office@dbmtalent.com directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-white">
      <Seo
        title="Contact"
        description="Contact David Ball Management. Representation enquiries, casting requests and general correspondence. Call +44 (0) 203 355 0561, email office@dbmtalent.com or write to 29 Holmdale Road, London BR7 6BY."
        path="/contact"
      />
      <section className="pt-40 pb-16 lg:pt-48 lg:pb-24 bg-[#001a38] text-white">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex items-center gap-4">
              <span className="w-10 h-px bg-[#C5A059]" />
              <span className="eyebrow text-[#C5A059]">Contact</span>
            </div>
          </Reveal>
          <div className="grid lg:grid-cols-12 gap-12 mt-8 items-end">
            <Reveal className="lg:col-span-8" delay={120}>
              <h1 className="font-heading uppercase text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9]">
                Let's<br /><span className="italic font-light text-[#C5A059]">talk.</span>
              </h1>
            </Reveal>
            <Reveal className="lg:col-span-4" delay={240}>
              <p className="text-white/75 leading-relaxed">
                Representation enquiries, casting requests and general correspondence.
                We reply to every message we receive.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-16">
          {/* Form */}
          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
              <div>
                <label className="eyebrow block mb-4">Enquiry Type</label>
                <div className="flex flex-wrap gap-2">
                  {ENQUIRY_TYPES.map((t) => (
                    <button
                      key={t.v}
                      type="button"
                      data-testid={`enquiry-${t.v}`}
                      onClick={() => setForm((f) => ({ ...f, enquiry_type: t.v }))}
                      className={`rounded-full px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase border transition-colors ${
                        form.enquiry_type === t.v
                          ? 'bg-[#002b5c] text-white border-[#002b5c]'
                          : 'border-black/15 text-[#333] hover:border-[#002b5c]'
                      }`}
                    >
                      {t.l}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Your Name" value={form.name} onChange={upd('name')} required testId="contact-name" />
                <Field label="Email" type="email" value={form.email} onChange={upd('email')} required testId="contact-email" />
                <Field label="Phone (optional)" value={form.phone} onChange={upd('phone')} testId="contact-phone" />
                <Field label="Subject" value={form.subject} onChange={upd('subject')} testId="contact-subject" />
              </div>

              <div>
                <label className="eyebrow block mb-3">Message</label>
                <textarea
                  data-testid="contact-message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={upd('message')}
                  className="w-full bg-transparent border-b border-black/20 focus:border-[#002b5c] outline-none py-3 text-[#333] resize-none transition-colors"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  data-testid="contact-consent"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#002b5c] cursor-pointer shrink-0"
                  required
                />
                <span className="text-sm text-[#4a4a4a] leading-relaxed">
                  I agree that David Ball Management may use the details above to reply to my enquiry.
                  I've read the <Link to="/privacy" className="gold-underline">privacy notice</Link>.
                </span>
              </label>

              {/* honeypot */}
              <input
                type="text"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.hp}
                onChange={upd('hp')}
                className="sr-only"
              />

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading || !consent}
                className="inline-flex items-center gap-3 rounded-full bg-[#002b5c] text-white px-9 py-4 text-[11px] tracking-[0.24em] uppercase hover:bg-[#001a38] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-500 hover:-translate-y-0.5"
              >
                {loading ? 'Sending…' : 'Send Message'} <Send size={14} />
              </button>
            </form>
          </Reveal>

          {/* Sidebar */}
          <Reveal className="lg:col-span-4 lg:col-start-9" delay={150}>
            <SectionEyebrow>David Ball Management · London</SectionEyebrow>
            <div className="mt-8 space-y-8">
              <Info icon={MapPin} title="Office">29 Holmdale Road<br />London, BR7 6BY<br />United Kingdom</Info>
              <Info icon={Phone} title="Telephone">
                <a href="tel:+442033550561" data-testid="contact-phone-link" className="gold-underline">+44 (0) 203 355 0561</a>
              </Info>
              <Info icon={Mail} title="Email">
                <a href="mailto:office@dbmtalent.com" data-testid="contact-email-link" className="gold-underline">office@dbmtalent.com</a>
              </Info>
              <Info icon={Clock} title="Hours">Mon – Fri · 09:00 – 18:00 GMT<br />By appointment only</Info>
            </div>
            <div className="hairline my-8" />
            <p className="eyebrow mb-4">Follow</p>
            <div className="flex items-center gap-4">
              <a href="https://www.instagram.com/dbmtalent/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-[#002b5c] hover:text-[#C5A059] transition-colors"><Instagram size={20} /></a>
              <a href="https://www.linkedin.com/company/dbm-talent/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-[#002b5c] hover:text-[#C5A059] transition-colors"><Linkedin size={20} /></a>
              <a href="https://www.facebook.com/davidballmanagement/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-[#002b5c] hover:text-[#C5A059] transition-colors"><Facebook size={20} /></a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Map */}
      <section className="w-full">
        <div className="w-full aspect-[21/9] bg-[#faf8f4]">
          <iframe
            data-testid="contact-map"
            title="David Ball Management London Office"
            src="https://www.google.com/maps?q=29+Holmdale+Road,+BR7+6BY,+London,+UK&output=embed"
            className="w-full h-full border-0 grayscale-[20%]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required = false, testId }) {
  return (
    <div>
      <label className="eyebrow block mb-3">{label}</label>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border-b border-black/20 focus:border-[#002b5c] outline-none py-3 text-[#333] transition-colors"
      />
    </div>
  );
}

function Info({ icon: Icon, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="w-10 h-10 rounded-full bg-[#002b5c]/5 flex items-center justify-center text-[#002b5c] shrink-0">
        <Icon size={16} strokeWidth={1.5} />
      </div>
      <div>
        <p className="eyebrow">{title}</p>
        <p className="mt-2 text-sm text-[#333] leading-relaxed">{children}</p>
      </div>
    </div>
  );
}
